import React, { useState, useEffect } from 'react';
import './HowToFundPopup.scss';
import Modal from '../../Modal/Modal';
import Accordion from "../../Accordion/Accordion";
import Select from "../../Form/Select/Select";
import withFeatureFlags from '../../FeatureFlags/withFeatureFlags';
import { productTypes } from "../../../utils/productHelpers";

/** @import {AccordionItem} from '../../Accordion/AccordionTypes' */
/** @import {HowToFundPopupProps} from './HowToFundPopupTypes' */
/** @import {WithFeatureFlagsProps} from '../../FeatureFlags/FeatureFlagsTypes' */
/** @import {Customer, Account} from '../DashboardTypes' */

import '../../Accordion/accordion.scss'
import './dropDown.scss'
import '../../MyDocuments/myDocuments.scss';
import BankIcon from '../../../assets/images/bank-thick.png';
import One from '../../../assets/images/icon-1.svg';
import Two from '../../../assets/images/icon-2.svg';

/** @returns {AccordionItem[]} */
function createItem(/** @type {HowToFundPopupProps} */ props) {
    return [{
        title: 'What are my linked account details',
        body: (
            <div className="pop-up__account-details pop-up__text">
                <div className="pop-up__row">
                    <div className="pop-up__column"> Account number:</div>
                    <div className="pop-up__column"> {props.data.customer.accounts[0].linkedAccountNumber}</div>
                </div>
                <div className="pop-up__row">
                    <div className="pop-up__column"> Sort code:</div>
                    <div className="pop-up__column"> {formatSortCode(props.data.customer.accounts[0].linkedAccountSortCode)}</div>
                </div>
            </div>
        )
    }]
}

const onExpandAccordion = (/** @type {{ target: HTMLInputElement | null; }} */ { target }) => {
    if (target?.checked) {
    }
};

const formatSortCode = (/** @type {string | undefined} */ sortCode) => {
    return sortCode && sortCode.length >= 6 ? sortCode.replace(/-/g, '').match(/.{1,2}/g)?.join('-') : sortCode;
};

function HowToFundPopup(/** @type {HowToFundPopupProps & WithFeatureFlagsProps} */ props) {
    const isMobile = window.innerWidth <= 768;
    const openAccounts = props.data.customer.accounts.filter(a => ((a.accountStatus === "FundingOpen") ||
        (a.product.productType === productTypes.EasyAccess && a.accountStatus !== "Closed") ||
        (a.product.productType === productTypes.Notice && a.accountStatus !== "Closed")));

    const [selectedAccount, setSelectedAccount] = useState(/** @type {Account | null} */ (null));

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    const setSelectedAccountState = (/** @type {string} */ selectedAccount, /** @type {Account[]} */ openAccounts) => {
        const account = openAccounts.find(a => a.accountNumber === selectedAccount);

        if (account == null)
            return;

        setSelectedAccount(account);
    };

    const mapAccountDropDown = (/** @type {Account[]} */ openAccounts) => {
        let mappedAccounts = openAccounts.map(function (account) {
            const info = {
                "label": `${account.product.name} - Account Number: ${account.accountNumber}`,
                "value": account.accountNumber
            }
            return info;
        });
        return mappedAccounts;
    };

    const renderAccountDetailsList = (/** @type {Account[]} */ openAccount) => {
        return (
            <div className="pop-up__selector-wrapper padded pop-up__text">
                <Select
                    name='account'
                    placeholder='Select'
                    options={mapAccountDropDown(openAccount)}
                    onChange={option => setSelectedAccountState(option?.value || "", openAccount)}
                    disabled={false}
                />
            </div>
        );
    }

    const renderSingleAccountDetails = (/** @type {Customer} */ customer, /** @type {Account} */ account) => {
        return (
            <div className="pop-up__account-details padded  pop-up__bold">
                <div className="pop-up__row">
                    <div className="pop-up__column"> Account holder:</div>
                    <div className="pop-up__column"> {customer.firstName} {customer.middleName} {customer.lastName}</div>
                </div>
                <div className="pop-up__row">
                    <div className="pop-up__column"> Sort code:</div>
                    <div className="pop-up__column"> {formatSortCode(account.sortCode)}</div>
                </div>
                <div className="pop-up__row">
                    <div className="pop-up__column"> Account number:</div>
                    <div className="pop-up__column"> {account.accountNumber}</div>
                </div>
                <div className="pop-up__row">
                    <div className="pop-up__column"> Reference:</div>
                    <div className="pop-up__column"> Investec</div>
                </div>
            </div>
        );
    }

    const getModalClassName = () => {
        return props.featureEnabled('EnableConfirmationOfPayee') ?
            "pop-up pop-up--small" : "pop-up";
    }

    return <Modal className={isMobile ? 'modal--mobile-top' : getModalClassName()} onClose={props.onClose}>
        <div className="modal__title  pop-up__title">How to add money from your linked account </div>
        <div className="pop-up__layout padded pop-up__text">
            <div className="pop-up__content-column">
                <div className="pop-up__content-row">
                    <div className="pop-up__content">
                        <img className="pop-up__number-icon" src={One}></img>
                    </div>
                    <div className="pop-up__content">
                        <div className="pop-up__subtitle"> Go and log in to your linked account</div>
                        <div className="pop-up__text"> You chose your linked account when you first opened an Investec Save account with us (although you might have updated it since then). To make a deposit from it, you'll need to log in to your account provider's banking app or website.</div>
                        <Accordion items={createItem(props)} 
                            classOverride="drop-down"
                            //@ts-expect-error this property does not exist 
                            className="drop-down padded"
                            onExpand={onExpandAccordion} />
                    </div>
                </div>
                <div className="pop-up__content-row">
                    <div className="pop-up__content">
                        <img className="pop-up__number-icon" src={Two}></img>
                    </div>
                    <div className="pop-up__content">
                        <div className="pop-up__subtitle"> Make a payment to the details below</div>
                        {openAccounts.length > 1 ? renderAccountDetailsList(openAccounts) : renderSingleAccountDetails(props.data.customer, openAccounts[0])}
                        {selectedAccount && renderSingleAccountDetails(props.data.customer, selectedAccount)}
                        <div className="pop-up__text"> Make sure you use the account holder name above. If you've never paid this account before, you'll need to create a new payee.</div>
                    </div>
                </div>
            </div>

            {!props.featureEnabled('EnableConfirmationOfPayee') &&
                <div className="pop-up__cop-warning">
                    <img className="pop-up__bank-icon" src={BankIcon} />
                    <div className="pop-up__subtitle"> Your bank might not be able to verify this account for you
                    </div>
                    <div>
                        <br />
                        <p>Some banks are using a new name-checking service to check the name of a payee’s account, to help you make sure you’re paying who you intend to pay.</p>
                        <br />
                        <p>We’re working to introduce the service but until we do your bank might not be able to check the name of our account. This won’t affect your ability to make deposits.</p>
                        <br />
                        <p>Please follow the payment instructions on this page carefully when you make your deposit.</p>
                    </div>
                </div>
            }
        </div>
    </Modal>;
}

export default withFeatureFlags(HowToFundPopup);
